import { isAdFreePath } from '../../../../lib/freestar-ad-free-paths';

/**
 * The ad-free path list for whichever app is running, published so that the *client* ad components
 * can consult it without every call site having to thread it down.
 *
 * Why this exists rather than React context: a slot is very often rendered by a server component in
 * a layout ABOVE the ad-free route — the footer ad comes from `(no-ticker)/layout.tsx` in
 * pickleball and straight from the root layout in team-leagues. Next preserves shared layouts
 * across a client-side navigation and never re-renders them, so any `adFree` value computed on the
 * server stays frozen at whatever the *previous* page decided. Navigating from a monetised page to
 * an ad-free one therefore left the footer slot mounted and serving.
 *
 * `CustomFreestarAd` and `AdPlaceholder` are client components, so a `usePathname()` call inside
 * them re-runs on every navigation even when their server-rendered parent did not re-render. All
 * they were missing was the list, which is a per-app constant — hence a plain module value rather
 * than a provider wrapping `{children}` in three root layouts.
 *
 * Safe on the server: every request in a given app registers the same array, and apps are separate
 * bundles. An app with no ad-free pages registers an empty list (clubs mounts the guard only for its
 * webview product teardown), `isAdFreePath` returns false and ads render exactly as they did before.
 */
const REGISTERED_AD_FREE_PAGES: { pages: readonly string[] } = { pages: [] };

/** Called by FreestarRouteGuard, which each ad-serving app mounts once in its root layout. */
export function registerAdFreePages(pages: readonly string[]): void {
	REGISTERED_AD_FREE_PAGES.pages = pages;
}

export function isRegisteredAdFreePath(pathname: string | null | undefined): boolean {
	return isAdFreePath(pathname, REGISTERED_AD_FREE_PAGES.pages);
}
